import React, { useMemo, useEffect, useState } from "react";
import {
  StyledParagraph,
  StyledTitle,
} from "../CustomAntdComponents/Typography";
import {
  Content,
  InfoWrapper,
  TextWrapper,
  Wrapper,
  AirportInfo,
  AirportItem,
  BoxesWrapper,
  BoxItem,
  StepContainer,
  ContentContainer,
  StatusBarWrapper,
  StatusBarIcon,
  StatusBarText,
  StatusBarIndicator,
  DashedBox,
  SkeletonLine,
  FloatingIconWrapper,
  CodeIconWrapper,
} from "./styles";
import { ReactComponent as RightArrowIcon } from "../../assets/images/arrow-right.svg";
import { ReactComponent as DirectionIcon } from "../../assets/images/direction-hr.svg";
import { ReactComponent as FlightIcon } from "../../assets/images/flight-trip.svg";
import { useFlightSearchStore } from "../../store/flightSearch/flightSearchStore";
import { pluralizeText } from "../../utils/helpers";
import { useStore } from "../../store/global/appStore";
import { getAirportStateName } from "../TripSummary/helper";

import {
  ClockCircleOutlined,
} from "@ant-design/icons";

import {
  Plain,
  VerifiedCheck,
  Tram,
  MapPoint,
  Telescope,
  LightbulbMinimalistic,
  Globus,
  ClockCircle,
} from "@solar-icons/react";
import { formatDisplayDate } from "../../screens/SearchListings/helpers";

type Props = {
  booking?: boolean;
};

/* ───── Status bar (reused across all 3 steps) ───── */
const StatusBar = ({
  text,
  icon,
}: {
  text: string;
  icon: React.ReactNode;
}) => (
  <StatusBarWrapper>
    <StatusBarIcon>{icon}</StatusBarIcon>
    <StatusBarText>{text}</StatusBarText>
    <div style={{ marginLeft: "auto", display: "flex", alignItems: "center" }}>
      <div style={{ width: "1px", height: "16px", background: "rgba(255,255,255,0.2)", marginRight: "12px" }} />
      <StatusBarIndicator>
        <div className="inner-square" />
      </StatusBarIndicator>
    </div>
  </StatusBarWrapper>
);

/* ───── Main component ───── */
const FlightRequestLoading = ({ booking }: Props) => {
  const [step, setStep] = useState(1);

  const { airports } = useStore();
  const {
    formData: {
      numberOfAdults,
      numberOfInfants,
      numberOfChildren,
      tripType,
      ReturnDate,
      DepartureDate,
      DestinationLocationCode,
      OriginLocationCode,
      FlightClass,
      multiCity,
    },
  } = useFlightSearchStore();

  const numberOfPassengers = useMemo(() => {
    return numberOfAdults + (numberOfChildren || 0) + (numberOfInfants || 0);
  }, [numberOfAdults, numberOfChildren, numberOfInfants]);

  const isRoundTrip = tripType === "round_trip";
  const isMultiCity = tripType === "multi_city" && multiCity?.length > 0;
  const departure = isMultiCity
    ? multiCity[0].OriginLocationCode
    : OriginLocationCode;
  const arrival = isMultiCity
    ? multiCity[multiCity.length - 1].DestinationLocationCode
    : DestinationLocationCode;
  const departureDate = isMultiCity
    ? multiCity[0].DepartureDate
    : DepartureDate;
  const arrivalDate = isMultiCity
    ? multiCity[multiCity.length - 1].DepartureDate
    : ReturnDate;

  useEffect(() => {
    const timer1 = setTimeout(() => setStep(2), 3000);
    const timer2 = setTimeout(() => setStep(3), 6000);

    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
    };
  }, []);

  return (
    <Wrapper>
      <Content>
        <ContentContainer>
          {/* ───── Step 1: Flight details card (existing card, just restyled) ───── */}
          <StepContainer active={step === 1}>
            <InfoWrapper>
              <AirportInfo>
                <AirportItem>
                  <StyledParagraph size="small" align="left">
                    {getAirportStateName(airports, departure)}
                  </StyledParagraph>
                  <StyledTitle>{departure}</StyledTitle>
                </AirportItem>

                {isRoundTrip ? <DirectionIcon /> : <RightArrowIcon />}

                <AirportItem>
                  <StyledParagraph size="small" align="right">
                    {getAirportStateName(airports, arrival)}
                  </StyledParagraph>
                  <StyledTitle>{arrival}</StyledTitle>
                </AirportItem>
              </AirportInfo>

              <TextWrapper>
                {(isRoundTrip || isMultiCity) && arrivalDate ? (
                  <StyledParagraph align="center">
                    {/* {departureDate?.format("ddd, DD MMM") +
                      " - " +
                      arrivalDate.format("ddd, DD MMM")} */}
                    {formatDisplayDate(departureDate) + " - " + formatDisplayDate(arrivalDate)}
                  </StyledParagraph>
                ) : (
                  <StyledParagraph align="center">
                      {/* {departureDate?.format("ddd, DD MMM")} */}
                      {formatDisplayDate(departureDate)}
                  </StyledParagraph>
                )}
              </TextWrapper>

              <TextWrapper>
                <StyledParagraph align="center">
                  {`${pluralizeText(
                    numberOfPassengers,
                    "passenger",
                    "passengers"
                  )}, ${FlightClass}`}
                </StyledParagraph>
              </TextWrapper>

              <BoxesWrapper>
                {new Array(30).fill(0).map((_, index) => (
                  <BoxItem key={index} index={index}>
                    &nbsp;
                  </BoxItem>
                ))}
              </BoxesWrapper>
            </InfoWrapper>

            <StatusBar
              text={
                booking
                  ? "Confirming your booking."
                  : "Fetching your itinerary."
              }
              icon={
                <FlightIcon
                  width={16}
                  height={16}
                  style={{ fill: "white" }}
                />
              }
            />
          </StepContainer>

          {/* ───── Step 2: Skeleton / code wireframe ───── */}
          <StepContainer active={step === 2}>
            <DashedBox>
              <SkeletonLine width="60%" />
              <SkeletonLine width="40%" />
              <SkeletonLine width="50%" />
              <SkeletonLine width="80%" />
              <SkeletonLine width="20%" />
              <div style={{ display: "flex", alignItems: "center", gap: "12px", marginTop: "4px" }}>
                <CodeIconWrapper>
                  {"</>"}
                </CodeIconWrapper>
                <SkeletonLine width="40%" height="16px" />
              </div>
            </DashedBox>

            <StatusBar
              text="Curating the best deals."
              icon={<VerifiedCheck weight="Outline" style={{ fontSize: 16 }} />}
            />
          </StepContainer>

          {/* ───── Step 3: Floating travel icons ───── */}
          <StepContainer active={step === 3}>
            <DashedBox>
              {/* Top-left: airplane */}
              <FloatingIconWrapper
                top="35px"
                left="65px"
                delay="0s"
                fontSize="28px"
              >
                <Plain weight="Outline" />
              </FloatingIconWrapper>

              {/* Top-right: badge/shield */}
              <FloatingIconWrapper
                top="40px"
                left="185px"
                delay="0.5s"
                fontSize="26px"
              >
                <VerifiedCheck weight="Outline" />
              </FloatingIconWrapper>

              {/* Center-left: telescope */}
              <FloatingIconWrapper
                top="110px"
                left="45px"
                delay="1s"
                fontSize="28px"
              >
                <Telescope weight="Outline" />
              </FloatingIconWrapper>

              {/* Center: bus/tram */}
              <FloatingIconWrapper
                top="95px"
                left="145px"
                delay="1.5s"
                fontSize="32px"
              >
                <Tram weight="Outline" />
              </FloatingIconWrapper>

              {/* Bottom-left: location pin */}
              <FloatingIconWrapper
                top="155px"
                left="100px"
                delay="2s"
                fontSize="28px"
              >
                <MapPoint weight="Outline" />
              </FloatingIconWrapper>

              {/* Bottom-right: lightbulb */}
              <FloatingIconWrapper
                top="145px"
                left="195px"
                delay="2.5s"
                fontSize="28px"
              >
                <LightbulbMinimalistic weight="Outline" />
              </FloatingIconWrapper>
            </DashedBox>

            <StatusBar
              text="Almost done."
              icon={<ClockCircle weight="Outline" style={{ fontSize: 16 }} />}
            />
          </StepContainer>
        </ContentContainer>
      </Content>
    </Wrapper>
  );
};

export default FlightRequestLoading;
